import { MagicClickOutlinedRounded } from '@Components/Icons';
import {
    CancelRounded,
    CancelScheduleSendRounded,
    DeleteOutlineRounded,
    DraftsRounded,
    GroupsRounded,
    MarkEmailReadRounded,
    ReportRounded,
    SendRounded,
    UnsubscribeRounded,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Stack,
    Tab,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import useHeader from '@hooks/useHeader';
import {
    Link,
    Outlet,
    useLinkProps,
    useNavigate,
    useParams,
} from '@tanstack/react-router';
import { emailSentQueryOptions } from '@utils/queryOptions';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import useDialog from '@hooks/useDialog';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { maxToneToLevel } from '@utils/colorUtils';
import { useState } from 'react';
import EmailContent from '@Components/Specific/EmailContent';

const statCards = [
    {
        key: 'recipients',
        path: '/emails/$emailId',
        title: 'Recipients',
        Icon: GroupsRounded,
    },
    {
        key: 'sent',
        path: 'sent',
        title: 'Sent',
        Icon: SendRounded,
    },
    {
        key: 'delivered',
        path: 'delivered',
        title: 'Delivered',
        Icon: MarkEmailReadRounded,
    },
    {
        key: 'opened',
        path: 'opened',
        title: 'Opened',
        Icon: DraftsRounded,
    },
    {
        key: 'clicked',
        path: 'clicked',
        title: 'Clicked',
        Icon: MagicClickOutlinedRounded,
    },
    {
        key: 'failed',
        path: 'failed',
        title: 'Failed',
        Icon: CancelRounded,
    },
    {
        key: 'bounced',
        path: 'bounced',
        title: 'Bounced',
        Icon: CancelScheduleSendRounded,
    },
    // {
    //     key: 'spam',
    //     path: 'spam',
    //     title: 'Spam',
    //     Icon: ReportRounded,
    // },

    {
        key: 'unsubscribed',
        path: 'unsubscribed',
        title: 'Unsubscribed',
        Icon: UnsubscribeRounded,
    },
];

const StatCard = ({ path, title, value, Icon }) => {
    const { t } = useTranslation();
    const linkProps = useLinkProps({
        to: path,
        activeOptions: { exact: true },
    });

    return (
        <Card
            variant="elevation"
            sx={{
                backgroundColor: 'surface.main',
                '&.active': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                },
            }}
            {...linkProps}
        >
            <CardActionArea>
                <CardContent sx={{ display: 'flex', gap: 2 }}>
                    <Avatar
                        sx={{
                            width: 48,
                            height: 48,
                            p: 1,
                            alignSelf: 'center',
                            backgroundColor: 'secondaryContainer.main',
                        }}
                    >
                        <Icon
                            sx={{
                                width: '100%',
                                height: '100%',
                                color: 'primary.main',
                            }}
                        />
                    </Avatar>
                    <Box>
                        <Typography variant="titleLarge">
                            {t('emailStats.' + title)}
                        </Typography>
                        <Typography variant="headlineLarge">
                            {value.toLocaleString()}
                        </Typography>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

const EmailsSentDetails = () => {
    const { t } = useTranslation();
    const { emailId } = useParams({});
    const navigate = useNavigate();
    const dialog = useDialog();

    const [tab, setTab] = useState('recipients');

    const { data: email } = useSuspenseQuery(emailSentQueryOptions(emailId));

    const deleteMutation = useMutation({
        mutationKey: ['deleteSentEmail'],
        onSuccess: () => {
            dialog.close();
            navigate({ to: '../' });
        },
    });

    const { mutate: duplicate, isPending: isDuplicating } = useMutation({
        mutationKey: ['duplicateSentEmail'],
        onSuccess: res => {
            dialog.close();
            navigate({ to: '/emails/drafts/' + res.id });
        },
    });

    useHeader({
        title: t('Emails Sent'),
        titleSuffix: email?.subject,
    });

    const quickOptions: QuickOption[] = [
        {
            label: t('Delete'),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
            onClick: () => {
                dialog?.open({
                    title: t('Delete Email') + '?',
                    message: t('prompts:content.deleteEmail'),
                    onConfirm: () => emailId,
                    confirmMutation: deleteMutation,
                    confirmLabel: t('Delete'),
                    confirmButtonProps: {
                        color: 'error',
                    },
                });
            },
        },
    ];

    return (
        <TabContext value={tab}>
            <Stack spacing={2}>
                <Card>
                    <CardContent sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={1}>
                            <CardMedia
                                component="img"
                                src={email.thumbnail}
                                alt="thumbnail"
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 4,
                                    bgcolor: email.background_color,
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    height: '100%',
                                    flexGrow: 1,
                                }}
                            >
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                        }}
                                    >
                                        <Typography variant="headlineLarge">
                                            {email.subject}
                                        </Typography>
                                        <QuickOptions options={quickOptions} />
                                    </Box>
                                    <Typography
                                        variant="headlineSmall"
                                        gutterBottom
                                    >
                                        {email.preview_text}
                                    </Typography>
                                    <Typography
                                        variant="bodyLarge"
                                        gutterBottom
                                    >
                                        {email.description}
                                    </Typography>
                                    <Typography variant="bodyLarge">
                                        {t('Sent')}:{' '}
                                        {email.sent_time.toContextualDateTime()}
                                    </Typography>
                                </Box>

                                <Stack
                                    direction="row"
                                    spacing={2}
                                    mt={2}
                                    // justifyContent="flex-end"
                                >
                                    <LoadingButton
                                        variant="tonal"
                                        size="large"
                                        onClick={() => duplicate(emailId)}
                                        loading={isDuplicating}
                                    >
                                        {t('Make a Copy')}
                                    </LoadingButton>
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                    <TabList
                        sx={{
                            borderTop: 1,
                            borderColor: 'divider',
                            mx: -0.75,
                        }}
                        onChange={(_, newValue) => setTab(newValue)}
                    >
                        <Tab label={t('Recipients')} value="recipients" />
                        <Tab label={t('Content')} value="content" />
                    </TabList>
                </Card>
                <TabPanel value="recipients" sx={{ p: 0 }}>
                    <Stack direction="row" spacing={2}>
                        <Stack spacing={2}>
                            {statCards.map(({ key, path, ...props }) => (
                                <StatCard
                                    key={key}
                                    {...props}
                                    path={path}
                                    value={email.stats?.[key] ?? -1}
                                />
                            ))}
                        </Stack>
                        <Outlet />
                    </Stack>
                </TabPanel>
                <TabPanel value="content" sx={{ p: 0 }}>
                    <EmailContent htmlContent={email?.body} />
                </TabPanel>
            </Stack>
        </TabContext>
    );
};

export default EmailsSentDetails;
